import React from 'react';
import { Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useTheme } from '../../../../theme/useTheme';

export interface OnePagerMetricItem {
	label: string;
	value: string;
	valueColor?: string;
}

export interface OnePagerMetricCardProps {
	title: string;
	subtitle: string;
	metrics: OnePagerMetricItem[];
	onPress?: () => void;
}

const OnePagerMetricCard: React.FC<OnePagerMetricCardProps> = ({
	title,
	subtitle,
	metrics,
	onPress,
}) => {
	const { colors, resolvedTheme } = useTheme();
	const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';

	return (
		<TouchableOpacity
			style={[styles.cardContainer, isDark && { backgroundColor: colors.background.surface }]}
			onPress={onPress}
			activeOpacity={0.85}
			disabled={!onPress}
		>
			{/* Title — full width at the top, shrinks font before truncating */}
			<Text
				style={styles.titleText}
				numberOfLines={1}
				adjustsFontSizeToFit
				minimumFontScale={0.72}
				// adjustsFontSizeToFit is a React Native built-in; on web it has no
				// effect but the text is still constrained by numberOfLines={1}.
			>
				{title}
			</Text>

			{/* Metric boxes — row below the title */}
			<View style={styles.metricsRow}>
				{metrics.map((metric, index) => (
					<View
						key={`${metric.label}-${index}`}
						style={[
							styles.metricBox,
							index > 0 && styles.metricBoxSpacing,
							isDark && { backgroundColor: colors.background.elevated },
						]}
					>
						<Text
							style={[styles.metricLabel, isDark && { color: colors.text.secondary }]}
							numberOfLines={1}
							adjustsFontSizeToFit
							minimumFontScale={0.75}
						>
							{metric.label}
						</Text>
						<Text
							style={[
								styles.metricValue,
								metric.valueColor ? { color: metric.valueColor } : null,
								isDark && !metric.valueColor && { color: colors.text.primary },
							]}
							numberOfLines={1}
						>
							{metric.value}
						</Text>
					</View>
				))}
			</View>
		</TouchableOpacity>
	);
};

const styles = StyleSheet.create({
	cardContainer: {
		backgroundColor: '#FFFFFF',
		borderRadius: 16,
		// Reduced from 16 → 12 so the three metric boxes have more horizontal room
		padding: 12,
		// Column layout: title on top, metric boxes below
		flexDirection: 'column',
		shadowColor: '#000',
		shadowOffset: { width: 0, height: 1 },
		shadowOpacity: 0.05,
		shadowRadius: 2,
		elevation: 1,
	},
	titleText: {
		fontSize: 14,
		fontWeight: '700',
		fontFamily: 'Inter-SemiBold',
		color: '#32363F',
		// No lineHeight — lets adjustsFontSizeToFit scale freely on native
		marginBottom: 8,
	},
	metricsRow: {
		flexDirection: 'row',
		alignItems: 'stretch',
		// Full card width so flex children divide it exactly
		width: '100%',
	},
	metricBox: {
		// flex:1 + flexBasis:0 → equal share of the available row width
		// flexShrink:1 + minWidth:0 → can shrink below content size if needed
		flex: 1,
		flexBasis: 0,
		flexShrink: 1,
		minWidth: 0,
		backgroundColor: '#F5F7FA',
		borderRadius: 12,
		height: 56,
		// Reduced horizontal padding so labels have more text room
		paddingHorizontal: 6,
		paddingVertical: 8,
		justifyContent: 'center',
		alignItems: 'center',
	},
	metricBoxSpacing: {
		// Reduced gap from 8 → 6 to recover a little extra width
		marginLeft: 6,
	},
	metricLabel: {
		fontSize: 10,
		fontFamily: 'Inter-SemiBold',
		color: '#7A7F87',
		// No fixed lineHeight — lets adjustsFontSizeToFit scale freely
		textTransform: 'uppercase',
		fontWeight: '700',
		textAlign: 'center',
	},
	metricValue: {
		marginTop: 3,
		fontSize: 13,
		fontFamily: 'Inter-Bold',
		lineHeight: 16,
		color: '#2F2F2F',
		fontWeight: '800',
		textAlign: 'center',
	},
});

OnePagerMetricCard.displayName = 'OnePagerMetricCard';

export default React.memo(OnePagerMetricCard);
